export interface ScenarioItem {
  id: string;
  scenario: string;
  scenarioTag: string;
  icon: string;
  xmValue: string;
  xmDetail: string;
  tickmillValue: string;
  tickmillDetail: string;
  winner: "xm" | "tickmill" | "tie";
  reason: string;
}

export const scenarioData: ScenarioItem[] = [
  {
    id: "scalping",
    scenario: "Heavy Scalping / High-Frequency",
    scenarioTag: "Scalping",
    icon: "ri-flashlight-line",
    xmValue: "EUR/USD spread ~0.6 pip (Ultra Low)",
    xmDetail: "Ultra Low at 0.6 pips with zero commission is the tightest pure-spread option. Standard account spreads of ~1.0 pip — decent but not leading. No dedicated raw-spread ECN account like Tickmill's Raw line.",
    tickmillValue: "EUR/USD spread from 0.0 pips (Raw Account)",
    tickmillDetail: "Raw account at $3/side ($6 round-turn) with raw interbank spreads from 0.0 pips. TradingView Raw at $3.5/side for chart-based scalping. True ECN execution with no dealing desk.",
    winner: "tickmill",
    reason: "Tickmill Wins: Raw Account at $6/lot round-turn with true ECN raw spreads. XM's best offering is Ultra Low at 0.6 pips pure spread — competitive for moderate traders but can't match Tickmill's raw interbank pricing for high-frequency scalping."
  },
  {
    id: "beginner",
    scenario: "Small Capital / Getting Started",
    scenarioTag: "Beginners",
    icon: "ri-seedling-line",
    xmValue: "Welcome bonus + deposit match, $5 min",
    xmDetail: "$30 no-deposit bonus + 100% welcome bonus up to $500. Start trading with as little as $5. Massive educational library with live webinars.",
    tickmillValue: "No deposit bonuses, $100 min deposit",
    tickmillDetail: "Classic account starts at $100. No promotional bonuses. Compensates with tight spreads and solid educational resources including webinars and eBooks.",
    winner: "xm",
    reason: "XM Wins: $5 minimum deposit + $30 no-deposit bonus makes XM the clear winner for beginners. Tickmill's $100 minimum and lack of bonuses create a higher barrier for new traders testing the waters."
  },
  {
    id: "swing",
    scenario: "Large Capital / Swing & Hold",
    scenarioTag: "Swing Trading",
    icon: "ri-moon-line",
    xmValue: "ASIC/CySEC regulated, standard swaps",
    xmDetail: "Multi-regulated with solid investor protection. Standard overnight swap fees at industry average. Reliable for multi-week and multi-month position holds.",
    tickmillValue: "FCA regulated, competitive swaps",
    tickmillDetail: "UK FCA regulation provides top-tier investor protection and FSCS coverage up to £85,000. Swap rates are competitive for swing traders holding positions.",
    winner: "tie",
    reason: "Too Close to Call: XM offers multi-jurisdiction security, while Tickmill's FCA regulation with FSCS protection gives UK/EU traders unmatched peace of mind. Choose based on your jurisdiction."
  },
  {
    id: "news",
    scenario: "News Trading / High-Impact Events",
    scenarioTag: "News Events",
    icon: "ri-newspaper-line",
    xmValue: "99.35% execution rate, near-zero requotes",
    xmDetail: "99.35% of orders executed in under 1 second. Rock-solid execution during NFP, FOMC, and black-swan events with strict slippage control.",
    tickmillValue: "ECN-grade execution, some slippage",
    tickmillDetail: "Excellent execution speeds under normal conditions. During extreme news events, occasional slippage of 1-3 pips reported on major pairs. ECN model provides transparency.",
    winner: "xm",
    reason: "XM Wins: Fear no NFP. XM's execution during news events is top-tier with explicit no-requote guarantees. Tickmill is solid but XM's consistency under extreme volatility is hard to beat."
  }
];

export interface IndicatorItem {
  label: string;
  icon: string;
  xmScore: number;
  xmNote: string;
  tickmillScore: number;
  tickmillNote: string;
}

export const indicatorData: IndicatorItem[] = [
  {
    label: "Deposit & Withdrawal Speed",
    icon: "ri-speed-line",
    xmScore: 8.5,
    xmNote: "Manual review, typically processed within hours on business days",
    tickmillScore: 8.0,
    tickmillNote: "Most methods processed same-day. Bank wires take 1-3 business days. No fees on either side."
  },
  {
    label: "Maximum Leverage",
    icon: "ri-rocket-line",
    xmScore: 8.0,
    xmNote: "1:1000 — stable and more than sufficient",
    tickmillScore: 8.0,
    tickmillNote: "Up to 1:1000 (FSA jurisdiction). 1:30 for FCA/CySEC retail clients."
  },
  {
    label: "Minimum Deposit",
    icon: "ri-wallet-3-line",
    xmScore: 9.5,
    xmNote: "$5 to start (Micro/Standard). $50 for Ultra Low. $10,000 for Shares.",
    tickmillScore: 6.5,
    tickmillNote: "$100 minimum for Classic/Raw/TradingView Raw accounts."
  },
  {
    label: "All-in Trading Cost (EUR/USD)",
    icon: "ri-money-dollar-circle-line",
    xmScore: 7.5,
    xmNote: "Ultra Low: 0.6 pips + $0 commission (~$6/lot). Standard/Micro: 1.0 pip + $0 (~$10/lot).",
    tickmillScore: 8.5,
    tickmillNote: "Raw: 0.0 pip avg + $3/side ($6/lot round-turn). TradingView Raw: 0.0 pip + $3.5/side ($7/lot). Classic: 1.6 pips + $0."
  },
  {
    label: "Regulatory Security",
    icon: "ri-shield-check-line",
    xmScore: 9.0,
    xmNote: "ASIC/CySEC/FSC multi-regulated, segregated funds, negative balance protection",
    tickmillScore: 9.5,
    tickmillNote: "FCA (UK), CySEC (EU), FSA (Seychelles), FSCA (South Africa), DFSA (Dubai). FSCS protection up to £85,000 for UK clients."
  },
  {
    label: "Platform & Tool Ecosystem",
    icon: "ri-tools-line",
    xmScore: 8.0,
    xmNote: "MT4/MT5 with TradingView integration. Free VPS, MQL5 community signals.",
    tickmillScore: 8.5,
    tickmillNote: "MT4/MT5, TradingView direct trading, Tickmill Trader proprietary app, Autochartist integration, copy trading tools."
  }
];

export interface DownsideItem {
  text: string;
  icon: string;
}

export interface BrokerHonestTake {
  pros: DownsideItem[];
  cons: DownsideItem[];
}

export const xmHonestTake: BrokerHonestTake = {
  pros: [
    {
      text: "$30 no-deposit bonus + 100% welcome bonus up to $500. Trade with the broker's money before risking your own.",
      icon: "ri-check-line"
    },
    {
      text: "99.35% order execution rate in under 1 second. Virtually zero requotes even during major news events.",
      icon: "ri-check-line"
    },
    {
      text: "Ultra Low account with zero commissions and spreads from 0.6 pips — the tightest pure-spread account. Standard accounts from 1.0 pip.",
      icon: "ri-check-line"
    },
    {
      text: "ASIC, CySEC, FSC multi-regulated. Segregated client funds and negative balance protection for maximum security.",
      icon: "ri-check-line"
    },
    {
      text: "Free VPS for EA traders + XMP loyalty points on every trade. Trade more, earn back more.",
      icon: "ri-check-line"
    }
  ],
  cons: [
    {
      text: "Standard account spreads are merely average at 1.6 pips. Without bonuses, XM isn't the cheapest on raw spread cost alone.",
      icon: "ri-emotion-sad-line"
    },
    {
      text: "Limited cryptocurrency pairs and higher stock CFD spreads compared to dedicated equity brokers.",
      icon: "ri-stock-line"
    },
    {
      text: "Bonus terms come with trading volume requirements. The free money isn't withdrawable until you trade enough lots.",
      icon: "ri-file-list-3-line"
    }
  ]
};

export const tickmillHonestTake: BrokerHonestTake = {
  pros: [
    {
      text: "Raw Account with $3/side commission — raw interbank spreads from 0.0 pips at $6/lot round-turn. Highly competitive ECN pricing.",
      icon: "ri-check-line"
    },
    {
      text: "TradingView Raw account — trade directly from TradingView charts with ECN raw spreads. Unique offering few brokers can match.",
      icon: "ri-check-line"
    },
    {
      text: "UK FCA regulation with FSCS protection up to £85,000. Five regulatory licenses: FCA, CySEC, FSA, FSCA, DFSA — covering UK, EU, South Africa, Dubai, and offshore.",
      icon: "ri-check-line"
    },
    {
      text: "True ECN execution across Classic, Raw, and TradingView Raw. No dealing desk intervention. Transparent pricing and fast fills for all account tiers.",
      icon: "ri-check-line"
    },
    {
      text: "Proprietary Tickmill Trader app with 60+ technical indicators, advanced charting, and biometric login. Plus full MT4/MT5 support.",
      icon: "ri-check-line"
    }
  ],
  cons: [
    {
      text: "$100 minimum deposit across Classic, Raw, and TradingView Raw accounts. Significantly higher barrier to entry than XM's $5 minimum.",
      icon: "ri-alert-line"
    },
    {
      text: "No deposit bonuses or promotional offers. If you're hunting for freebies and welcome bonuses, Tickmill offers absolutely nothing across any account tier.",
      icon: "ri-gift-line"
    },
    {
      text: "$10 quarterly inactivity fee after 12 months of no trading. While not aggressive, seasonal traders who go dormant should be aware of this charge.",
      icon: "ri-time-line"
    }
  ]
};

export interface AccountMatchup {
  id: string;
  title: string;
  subtitle: string;
  xmAccount: {
    name: string;
    spread: string;
    commission: string;
    minDeposit: string;
    leverage: string;
    highlight: string;
    pros: string[];
  };
  tickmillAccount: {
    name: string;
    spread: string;
    commission: string;
    minDeposit: string;
    leverage: string;
    highlight: string;
    pros: string[];
  };
  analysis: string;
}

export const accountMatchups: AccountMatchup[] = [
  {
    id: "lowcost",
    title: "The Low-Cost Battle: Who's Cheaper Per Trade?",
    subtitle: "XM Ultra Low Account vs Tickmill Raw Account — we run the numbers so you don't have to",
    xmAccount: {
      name: "XM Ultra Low",
      spread: "EUR/USD from 0.6 pips",
      commission: "Zero commission",
      minDeposit: "$50",
      leverage: "1:1000",
      highlight: "Pure spread model — zero commission",
      pros: ["Zero commission — all cost is the spread", "Lowest-cost XM spread-only account at 0.6 pips", "$50 minimum deposit", "1,000+ instruments across all asset classes"]
    },
    tickmillAccount: {
      name: "Tickmill Raw",
      spread: "EUR/USD from 0.0 pips, avg 0.1",
      commission: "$3/side ($6/lot round-turn)",
      minDeposit: "$100",
      leverage: "1:1000",
      highlight: "ECN raw spreads — $3/side commission",
      pros: ["Raw interbank spreads from 0.0 pips", "$3/side — all-in ~$6/lot for active traders", "Same institutional liquidity with full MT4/MT5 access", "True ECN — no dealing desk intervention"]
    },
    analysis: "Here's the math: XM Ultra Low at 0.6 pips = ~$6/lot all-in. Tickmill Raw at avg 0.1 pips + $6/lot commission = ~$7/lot all-in on EUR/USD. XM Ultra Low is actually cheaper for lower-volume traders, while Tickmill Raw becomes competitive at higher volumes with tighter raw spreads. For pure spread cost, XM Ultra Low wins at 0 commission. But for scalpers who need raw pricing transparency, Tickmill Raw's ECN model may be preferred despite the slight all-in cost difference."
  },
  {
    id: "popular",
    title: "The Popular Choice: Which Flagship Account Wins?",
    subtitle: "XM Standard Account vs Tickmill Classic Account — the most-used accounts, head to head",
    xmAccount: {
      name: "XM Standard",
      spread: "EUR/USD from 1.0 pip",
      commission: "Zero commission",
      minDeposit: "$5",
      leverage: "1:1000",
      highlight: "XM's most popular — bonus eligible",
      pros: ["Zero commission spread-only model", "Eligible for $30 no-deposit + 100% welcome bonus", "Full 1 standard lot (100,000 units) trading", "Rock-solid 99.35% execution rate"]
    },
    tickmillAccount: {
      name: "Tickmill Classic",
      spread: "EUR/USD from 1.6 pips",
      commission: "Zero commission",
      minDeposit: "$100",
      leverage: "1:1000",
      highlight: "Tickmill's entry — true ECN backbone",
      pros: ["Zero commission — all cost is the spread", "True ECN execution engine — no dealing desk", "Full MT4/MT5 + Autochartist included", "FCA-regulated with FSCS protection"]
    },
    analysis: "For the average retail trader doing a few trades per day, XM Standard wins on value: $5 entry, 1.0 pip spread, plus bonus perks. Tickmill Classic at 1.6 pips with $100 minimum is a steeper entry. However, Tickmill Classic uses the same ECN infrastructure as the Raw account — meaning your fills come from real liquidity providers, not a dealer's book. For traders who prioritize execution quality over bonuses, Tickmill's transparency is worth the premium."
  },
  {
    id: "raw",
    title: "The Cost Showdown: Spread-Only vs ECN",
    subtitle: "XM's tightest spread-only account vs Tickmill's raw ECN — which pricing model fits your trading style?",
    xmAccount: {
      name: "XM Ultra Low",
      spread: "EUR/USD from 0.6 pips",
      commission: "Zero commission",
      minDeposit: "$50",
      leverage: "1:1000",
      highlight: "Tightest XM spreads — zero commission",
      pros: ["Spreads from 0.6 pips — XM's tightest", "Zero commission — all cost is the spread", "$50 minimum deposit", "MT4/MT5 access with 1,000+ instruments"]
    },
    tickmillAccount: {
      name: "Tickmill Raw",
      spread: "EUR/USD from 0.0 pips",
      commission: "$3/side ($6/lot round-turn)",
      minDeposit: "$100",
      leverage: "1:1000",
      highlight: "Raw ECN — $3/side commission",
      pros: ["Raw interbank spreads from 0.0 pips", "$3/side — $6/lot all-in round-turn", "MT4 and MT5 platform access", "All trading strategies and EAs allowed"]
    },
    analysis: "Different pricing models, different trader profiles. XM Ultra Low at 0.6 pips pure spread = ~$6/lot all-in. Tickmill Raw at avg 0.1 pips + $6/lot commission = ~$7/lot all-in on EUR/USD. At moderate volumes (1-10 lots/day), XM Ultra Low is actually cheaper and simpler — no commission math. But Tickmill Raw's true ECN model delivers raw interbank pricing with ~$6/lot for active traders, and the TradingView Raw variant adds chart-to-trade execution. For spread-only simplicity, XM Ultra Low wins. For raw ECN transparency, Tickmill Raw is the superior platform."
  }
];

export interface TechComparison {
  category: string;
  icon: string;
  xm: { label: string; detail: string; score: number };
  tickmill: { label: string; detail: string; score: number };
}

export const techData: TechComparison[] = [
  {
    category: "Trading Platform Options",
    icon: "ri-computer-line",
    xm: { label: "MT4/MT5 + TradingView", detail: "Full MT4 and MT5 support with MQL community signals. TradingView integration for chart-based analysis. No proprietary platform.", score: 7.5 },
    tickmill: { label: "MT4/MT5 + TradingView Direct + Proprietary App", detail: "MT4/MT5 plus TradingView direct trading via dedicated Raw account. Tickmill Trader proprietary mobile app with 60+ indicators and biometric login. Autochartist integration for pattern recognition.", score: 8.5 }
  },
  {
    category: "Copy Trading Ecosystem",
    icon: "ri-group-line",
    xm: { label: "MQL5 Community Reliant", detail: "Copy trading primarily through MQL5 community signals. No dedicated proprietary copy trading platform. Functional but not specialized.", score: 5.5 },
    tickmill: { label: "Tickmill Social Trading Platform", detail: "Tickmill Social Trading allows users to follow strategy providers. Multiple signal providers with verified track records. Dedicated follower and strategy provider roles.", score: 7.5 }
  },
  {
    category: "Mobile App Experience",
    icon: "ri-smartphone-line",
    xm: { label: "Standard MT4/MT5 Mobile", detail: "Relies on standard MetaTrader mobile apps. Solid for order management and basic charting but no proprietary mobile experience.", score: 7.0 },
    tickmill: { label: "Tickmill Trader + MT Apps", detail: "Proprietary Tickmill Trader app alongside MT4/MT5 mobile. Cleaner interface with faster account management, 60+ technical indicators, and biometric login for security.", score: 8.5 }
  },
  {
    category: "API & Automation Support",
    icon: "ri-code-line",
    xm: { label: "MT4/MT5 Standard API", detail: "Full MQL4/MQL5 support with rich community resources. Free VPS for EA traders. No standalone REST API.", score: 7.5 },
    tickmill: { label: "MT4/MT5 + EAs Fully Supported", detail: "Full MQL support for expert advisors. No restrictions on automated strategies or scalping EAs. VPS service available for 24/7 EA operation.", score: 7.5 }
  }
];

export interface PerkItem {
  icon: string;
  title: string;
  description: string;
}

export const xmPerks: PerkItem[] = [
  {
    icon: "ri-gift-2-line",
    title: "$30 No-Deposit Bonus",
    description: "Claim it right after verification. Trade real markets without depositing a cent first. The ultimate risk-free test drive."
  },
  {
    icon: "ri-vip-diamond-line",
    title: "Loyalty Points Program (XMP)",
    description: "Earn points on every trade. Redeem for cash or bonus credit. The more you trade, the more you earn back — effectively lowering your spread costs."
  },
  {
    icon: "ri-cloud-line",
    title: "Free VPS Service",
    description: "Qualify with minimum deposit and volume requirements. Perfect for traders running EAs 24/7 without keeping their PC on."
  },
  {
    icon: "ri-percent-line",
    title: "100% Welcome Deposit Bonus",
    description: "Double your first deposit up to $500. Deposit $500, trade with $1,000. The most aggressive bonus in the industry."
  }
];

export const tickmillPerks: PerkItem[] = [
  {
    icon: "ri-money-dollar-circle-line",
    title: "Raw Account — $3/Side Commission",
    description: "Tickmill's flagship ECN account. Raw interbank spreads from 0.0 pips at $3/side ($6 round-turn). True ECN execution with no dealing desk for maximum pricing transparency."
  },
  {
    icon: "ri-line-chart-line",
    title: "TradingView Raw — Chart-to-Trade",
    description: "Execute trades directly from TradingView charts via a dedicated Raw account. One-click trading from your favorite charting platform with ECN pricing at $3.5/side."
  },
  {
    icon: "ri-shield-star-line",
    title: "FCA + FSCS Protection",
    description: "UK FCA regulation with Financial Services Compensation Scheme coverage up to £85,000. Five regulatory licenses across FCA, CySEC, FSA, FSCA, and DFSA — industry-leading fund security."
  },
  {
    icon: "ri-smartphone-line",
    title: "Tickmill Trader Proprietary App",
    description: "Feature-rich mobile app with 60+ technical indicators, advanced charting tools, biometric login, and social trading. Available on iOS and Android for trading on the go."
  }
];

export interface BlackSwanPoint {
  category: string;
  xm: string;
  tickmill: string;
  xmAdvantage: boolean;
}

export const blackSwanData: BlackSwanPoint[] = [
  {
    category: "Order Execution (News Events)",
    xm: "99.35% of orders executed in under 1 second. Zero requotes policy. Rock-solid during NFP, FOMC, and black-swan market events.",
    tickmill: "Strong ECN execution under normal conditions. During extreme volatility, occasional 1-3 pip slippage on major pairs reported. No requote policy but fills may slip.",
    xmAdvantage: true
  },
  {
    category: "Slippage Management",
    xm: "Explicitly guarantees no requotes. Strict slippage control. Most orders filled at requested price or better.",
    tickmill: "ECN model means fills reflect real market conditions. Positive slippage possible, negative slippage also occurs during fast markets. Transparent but less controlled.",
    xmAdvantage: true
  },
  {
    category: "Leverage Stability During Crises",
    xm: "Leverage parameters remain stable during extreme conditions. Traders can rely on pre-set risk parameters without surprise adjustments.",
    tickmill: "Dynamic leverage adjusts with market exposure. During black-swan events, leverage on certain instruments may be reduced temporarily as a risk management measure.",
    xmAdvantage: true
  },
  {
    category: "Fund Security & Compensation",
    xm: "Segregated client funds and negative balance protection. Solid multi-jurisdiction regulatory framework.",
    tickmill: "FCA regulation provides FSCS compensation up to £85,000 for UK clients. Segregated accounts at Tier-1 banks. Industry-leading fund protection.",
    xmAdvantage: false
  }
];

export interface QuizQuestion {
  id: string;
  question: string;
  options: { label: string; value: string }[];
}

export const quizQuestions: QuizQuestion[] = [
  {
    id: "capital",
    question: "What's your starting capital?",
    options: [
      { label: "Under $100 — Just testing the waters", value: "small" },
      { label: "$100 - $5,000 — Got a decent budget", value: "medium" },
      { label: "$5,000+ — Professional level", value: "large" }
    ]
  },
  {
    id: "style",
    question: "What's your trading style?",
    options: [
      { label: "Scalping / High-frequency — Dozens to hundreds of trades daily", value: "scalping" },
      { label: "Day trading — A few trades per day", value: "daytrade" },
      { label: "Swing trading / Running EAs — Holding days to weeks", value: "swing" }
    ]
  },
  {
    id: "priority",
    question: "What matters most to you?",
    options: [
      { label: "Lowest possible trading costs — every pip counts", value: "cost" },
      { label: "Bonuses, promotions, and free money to boost my account", value: "bonus" },
      { label: "Maximum regulatory security and fund protection", value: "safety" }
    ]
  }
];

export function computeQuizResult(answers: Record<string, string>): { broker: "xm" | "tickmill"; percentage: number; reason: string } {
  const { capital, style, priority } = answers;
  let xmScore = 0;
  let tickmillScore = 0;

  if (style === "scalping") {
    tickmillScore += 35;
    xmScore += 10;
  } else if (style === "daytrade") {
    tickmillScore += 20;
    xmScore += 20;
  } else {
    xmScore += 20;
    tickmillScore += 10;
  }

  if (capital === "small") {
    xmScore += 30;
    tickmillScore += 5;
  } else if (capital === "large") {
    tickmillScore += 20;
    xmScore += 10;
  } else {
    xmScore += 15;
    tickmillScore += 15;
  }

  if (priority === "cost") {
    tickmillScore += 25;
    xmScore += 5;
  } else if (priority === "bonus") {
    xmScore += 30;
    tickmillScore += 0;
  } else {
    tickmillScore += 15;
    xmScore += 15;
  }

  const total = xmScore + tickmillScore;
  if (xmScore >= tickmillScore) {
    return {
      broker: "xm",
      percentage: Math.round((xmScore / total) * 100),
      reason: xmScore > tickmillScore * 1.2
        ? "Your trading style is a strong match for XM: low entry barrier, generous bonuses, and rock-solid execution. Perfect for beginners, bonus hunters, and safety-first traders."
        : "You lean toward XM, but Tickmill is a solid alternative. XM's $5 minimum deposit and bonus program give you an edge — start with the $30 no-deposit bonus to test the waters."
    };
  }
  return {
    broker: "tickmill",
    percentage: Math.round((tickmillScore / total) * 100),
    reason: tickmillScore > xmScore * 1.3
      ? "Your trading style is built for Tickmill: Raw account at $3/side ($6 round-turn) with 0.0 pip ECN spreads, TradingView Raw for chart-to-trade workflow, and FCA regulation with FSCS protection. Cost-conscious and high-frequency traders will find their optimal setup here."
      : "You lean toward Tickmill, but XM's bonuses and lower minimum deposit are worth considering. Try Tickmill's Raw account at $3/side commission for transparent ECN pricing."
  };
}

export interface AccountTypeItem {
  name: string;
  highlight: string;
  spread: string;
  commission: string;
  minDeposit: string;
  leverage: string;
  note: string;
  features: string[];
  bestFor: string;
}

export const xmAccountTypes: AccountTypeItem[] = [
  {
    name: "Micro Account",
    highlight: "Ultra-low entry barrier",
    spread: "From 1.0 pip (EUR/USD)",
    commission: "Zero commission",
    minDeposit: "$5",
    leverage: "Up to 1:1000",
    note: "Contracts are 1,000 base currency units (1 micro lot). Perfect for absolute beginners who want to learn with minimal risk. Same execution quality as larger accounts. Eligible for XM bonuses including the $30 no-deposit bonus.",
    features: [
      "Trade with as little as $5 — the industry's lowest barrier",
      "1 micro lot = 1,000 units, ideal for position sizing practice",
      "Full MT4/MT5 access with all 1,000+ instruments",
      "Negative balance protection included standard",
      "Eligible for all XM bonuses and promotions"
    ],
    bestFor: "Complete beginners testing the waters with pocket change"
  },
  {
    name: "Standard Account",
    highlight: "Most popular — bonus eligible",
    spread: "From 1.0 pip (EUR/USD)",
    commission: "Zero commission",
    minDeposit: "$5",
    leverage: "Up to 1:1000",
    note: "XM's flagship account. 1 standard lot = 100,000 units. No commissions, just the raw spread. Eligible for all XM bonuses including $30 no-deposit and 100% welcome match. Reliable spread-only model backed by 99.35% execution rates.",
    features: [
      "Zero commission — pay only the spread, nothing more",
      "1 standard lot = 100,000 units, full-size trading",
      "Eligible for all bonuses: $30 no-deposit + 100% welcome match",
      "Free daily market analysis and webinars",
      "XMP loyalty points earned on every trade"
    ],
    bestFor: "The everyday trader who wants bonus perks + no commission complexity"
  },
  {
    name: "Ultra Low Account",
    highlight: "Tightest XM spreads — zero commission",
    spread: "From 0.6 pips (EUR/USD)",
    commission: "Zero commission",
    minDeposit: "$50",
    leverage: "Up to 1:1000",
    note: "XM's tightest spread offering with zero commissions. Spreads start at 0.6 pips on EUR/USD. Available in both Micro and Standard lot sizes. Note: Bonuses are NOT available on Ultra Low accounts.",
    features: [
      "Spreads from 0.6 pips — XM's tightest pure-spread offering",
      "Zero commission — all cost is in the spread",
      "Available in Micro + Standard lot sizes",
      "Same 1,000+ instruments and platform access",
      "Not eligible for bonuses — pure pricing only"
    ],
    bestFor: "Cost-aware traders wanting tight spreads without commission math"
  },
  {
    name: "Shares Account",
    highlight: "1,200+ global stocks — exchange pricing",
    spread: "Exchange-level real stock spreads",
    commission: "Per-share commission (varies by exchange)",
    minDeposit: "$10,000",
    leverage: "No leverage (physical shares)",
    note: "XM's dedicated stock trading account. Direct access to 1,200+ individual shares across US, UK, and EU exchanges — not CFDs, actual share trading with ownership rights. Leverage is not available. Designed for investors who want exchange-grade pricing and dividend entitlements.",
    features: [
      "Trade 1,200+ individual shares across global exchanges",
      "Exchange-grade pricing with transparent per-share commissions",
      "Dividend rights included on long positions",
      "Access to US, UK, EU stock exchanges",
      "Physical share ownership — different from CFDs"
    ],
    bestFor: "Stock investors wanting real exchange access, not CFD exposure"
  }
];

export const tickmillAccountTypes: AccountTypeItem[] = [
  {
    name: "Classic Account",
    highlight: "Spread-only — zero commission",
    spread: "From 1.6 pips (EUR/USD)",
    commission: "Zero commission",
    minDeposit: "$100",
    leverage: "Up to 1:1000",
    note: "Tickmill's beginner-friendly account. Zero commission with spreads starting from 1.6 pips — all cost baked into the spread. Available on MT4 and MT5. Ideal for traders who prefer straightforward, commission-free pricing. Islamic swap-free version available on request.",
    features: [
      "Zero commission — pay only the spread, nothing else",
      "Available on MT4 and MT5 platforms",
      "All trading strategies allowed — scalping, hedging, EAs",
      "Islamic swap-free version available on request",
      "Minimum lot size 0.01"
    ],
    bestFor: "Beginners and casual traders who prefer simple, commission-free pricing"
  },
  {
    name: "Raw Account",
    highlight: "Raw ECN — $3/side commission",
    spread: "From 0.0 pips (EUR/USD)",
    commission: "$3 per side ($6 per round turn)",
    minDeposit: "$100",
    leverage: "Up to 1:1000",
    note: "Tickmill's flagship ECN account. Raw interbank spreads from 0.0 pips with a $3 per side commission on MT4 and MT5. This is Tickmill's most popular account for active traders who demand tight spreads and transparent commission-based pricing.",
    features: [
      "Raw interbank spreads from 0.0 pips — true ECN pricing",
      "$3 per side commission ($6 round-turn) on MT4/MT5",
      "All trading strategies allowed with no restrictions",
      "Islamic swap-free version available on request",
      "Minimum lot size 0.01"
    ],
    bestFor: "Active traders who want raw spreads with transparent commission pricing"
  },
  {
    name: "TradingView Raw",
    highlight: "One-click trade from TradingView charts",
    spread: "From 0.0 pips (EUR/USD)",
    commission: "$3.5 per side ($7 per round turn)",
    minDeposit: "$100",
    leverage: "Up to 1:1000",
    note: "A dedicated Tickmill account for TradingView users. Same Raw ECN pricing and execution, but built exclusively for the TradingView ecosystem. Execute trades directly from your TradingView charts. Available base currency: USD only. Requires a separate account in your Tickmill client area.",
    features: [
      "One-click trade execution directly from TradingView charts",
      "Same Raw ECN pricing — 0.0 pip spreads + $3.5/side",
      "Use all your TradingView indicators and strategies",
      "Separate account — can run alongside MT4/MT5 accounts",
      "Available base currency: USD only"
    ],
    bestFor: "TradingView power users who want seamless chart-to-trade workflow"
  }
];